import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { Loader2 } from "lucide-react";

type Prefs = {
  language: "en" | "fr";
  notifications: { email: boolean; push: boolean; sound: boolean };
};

const DEFAULTS: Prefs = {
  language: "en",
  notifications: { email: true, push: false, sound: true },
};

export function ProfilePreferences({
  initial,
  onSave,
  saving,
}: {
  initial?: Partial<Prefs>;
  onSave: (prefs: Prefs) => Promise<void>;
  saving: boolean;
}) {
  const { language, setLanguage } = useLanguage() as any;
  const { toast } = useToast();
  const [prefs, setPrefs] = useState<Prefs>({ ...DEFAULTS, ...(initial || {}) } as Prefs);

  useEffect(() => {
    if (initial) setPrefs({ ...DEFAULTS, ...initial, notifications: { ...DEFAULTS.notifications, ...(initial as any).notifications } });
  }, [initial]);

  const t = language === "fr"
    ? { title: "Préférences", lang: "Langue", notif: "Notifications", email: "Courriel", push: "Push (navigateur)", sound: "Sons", save: "Enregistrer", saved: "Préférences enregistrées" }
    : { title: "Preferences", lang: "Language", notif: "Notifications", email: "Email", push: "Push (browser)", sound: "Sounds", save: "Save", saved: "Preferences saved" };

  const handleSave = async () => {
    await onSave(prefs);
    if (typeof setLanguage === "function") setLanguage(prefs.language);
    toast({ title: t.saved });
  };

  return (
    <Card>
      <CardHeader><CardTitle>{t.title}</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div className="max-w-xs">
          <Label>{t.lang}</Label>
          <Select value={prefs.language} onValueChange={(v) => setPrefs((p) => ({ ...p, language: v as "en" | "fr" }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <Label className="text-base">{t.notif}</Label>
          {(["email", "push", "sound"] as const).map((k) => (
            <div key={k} className="flex items-center justify-between rounded-md border p-3">
              <span className="text-sm">{t[k]}</span>
              <Switch
                checked={prefs.notifications[k]}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, notifications: { ...p.notifications, [k]: v } }))}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t.save}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
